// /frontend/src/pages/OEPHub.jsx
// OEP Retention Machine - Complete revamp for tracking and retaining clients
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { csrfHeadersFor } from '../api';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield, Users, TrendingDown, TrendingUp, Send, CheckCircle2,
  XCircle, Clock, Phone, Mail, MessageSquare, ChevronRight, ChevronDown,
  RefreshCw, Calendar, UserPlus, UserMinus, ArrowRight, Sparkles,
  Filter, Search, AlertTriangle, Award, Heart, CheckCheck, Circle,
  Loader2, ExternalLink, MailCheck, MessageCircle, Trash2, X, BellOff, Bell
} from "lucide-react";

// Brand colors
const brand = {
  navy: "#172A3A",
  gold: "#FFB800",
  slate: "#20344A",
};

// ============================================================================
// API FUNCTIONS
// ============================================================================
const BASE = process.env.REACT_APP_API_URL || "";

async function fetchWithAuth(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const res = await fetch(`${BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers, ...csrfHeadersFor(method) }
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const api = {
  getSeason: () => fetchWithAuth('/oep/season'),
  getCohort: (seasonYear) => fetchWithAuth(`/oep/cohort${seasonYear ? `?seasonYear=${seasonYear}` : ''}`),
  getAnalytics: (seasonYear) => fetchWithAuth(`/oep/analytics${seasonYear ? `?seasonYear=${seasonYear}` : ''}`),
  getActivity: (limit = 20) => fetchWithAuth(`/oep/activity?limit=${limit}`),
  getAutomations: () => fetchWithAuth('/oep/automations'),
  updateAutomations: (settings) => fetchWithAuth('/oep/automations', { method: 'PUT', body: JSON.stringify(settings) }),
  autoPopulateCohort: (seasonYear) => fetchWithAuth('/oep/cohort/auto-populate', { method: 'POST', body: JSON.stringify({ seasonYear }) }),
  sendFollowUp: (cohortId, data) => fetchWithAuth(`/oep/cohort/${cohortId}/send`, { method: 'POST', body: JSON.stringify(data) }),
  updateCohortMember: (cohortId, data) => fetchWithAuth(`/oep/cohort/${cohortId}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeCohortMember: (cohortId) => fetchWithAuth(`/oep/cohort/${cohortId}`, { method: 'DELETE' }),
  removeDuplicates: (seasonYear) => fetchWithAuth('/oep/cohort/remove-duplicates', { method: 'POST', body: JSON.stringify({ seasonYear }) }),
  clearCohort: (seasonYear) => fetchWithAuth('/oep/cohort/clear', { method: 'POST', body: JSON.stringify({ seasonYear }) }),
  getEligibleClients: (seasonYear) => fetchWithAuth(`/oep/eligible-clients${seasonYear ? `?seasonYear=${seasonYear}` : ''}`),
  getClients: () => fetchWithAuth('/clients?limit=500'),
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function getOEPSeason(now = new Date()) {
  const y = now.getFullYear();
  const inCurrentOEP = now.getMonth() <= 2; // Jan=0, Feb=1, Mar=2
  const seasonYear = inCurrentOEP ? y : y + 1;
  const start = new Date(seasonYear, 0, 1);
  const end = new Date(seasonYear, 2, 31, 23, 59, 59, 999);
  return { seasonYear, start, end, inCurrentOEP: now >= start && now <= end };
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPhone(phone) {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

function getEffectiveYearClients(clients, year) {
  return clients.filter(c => {
    if (!c.effectiveDate) return false;
    const effDate = new Date(c.effectiveDate);
    return effDate.getFullYear() === year && effDate.getMonth() === 0 && effDate.getDate() === 1;
  });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function OEPHub() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [now] = useState(new Date());
  const localSeason = useMemo(() => getOEPSeason(now), [now]);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cohort, setCohort] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [activity, setActivity] = useState([]);
  const [automations, setAutomations] = useState({});
  const [seasonYear, setSeasonYear] = useState(localSeason.seasonYear);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingTo, setSendingTo] = useState(null);
  const [showClientModal, setShowClientModal] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [sendingReferral, setSendingReferral] = useState(false);
  const [referralResult, setReferralResult] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [cohortRes, analyticsRes, activityRes, automationsRes, clientsRes] = await Promise.all([
        api.getCohort(seasonYear).catch(() => ({ data: [] })),
        api.getAnalytics(seasonYear).catch(() => ({ data: {} })),
        api.getActivity(50).catch(() => ({ data: [] })),
        api.getAutomations().catch(() => ({ data: {} })),
        api.getClients().catch(() => [])
      ]);

      setCohort(cohortRes.data || []);
      setAnalytics(analyticsRes.data || {});
      setActivity(activityRes.data || []);
      setAutomations(automationsRes.data || {});
      setAllClients(Array.isArray(clientsRes) ? clientsRes : clientsRes.data || []);
    } catch (err) {
      console.error('OEP load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [seasonYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get clients with Jan 1 effective dates
  const jan1Clients = useMemo(() => {
    // Clients with 1/1/YEAR effective date
    return allClients.filter(c => {
      if (!c.effectiveDate) return false;
      const d = new Date(c.effectiveDate);
      return d.getMonth() === 0 && d.getDate() === 1 && d.getFullYear() === seasonYear;
    });
  }, [allClients, seasonYear]);

  // Categorize cohort by outreach status
  const categorizedCohort = useMemo(() => {
    const needsJan2 = cohort.filter(c => !c.jan2Sent && c.outreachPlan?.jan2 !== false);
    const needsFeb1 = cohort.filter(c => c.jan2Sent && !c.feb1Sent && c.outreachPlan?.feb1 !== false);
    const needsMar1 = cohort.filter(c => c.jan2Sent && c.feb1Sent && !c.mar1Sent && c.outreachPlan?.mar1 !== false);
    const allComplete = cohort.filter(c => c.jan2Sent && c.feb1Sent && c.mar1Sent);
    const active = cohort.filter(c => c.status === 'Active');
    const switched = cohort.filter(c => c.status === 'Switched');
    const lost = cohort.filter(c => c.status === 'Cancelled' || c.status === 'Lost');

    return { needsJan2, needsFeb1, needsMar1, allComplete, active, switched, lost };
  }, [cohort]);

  // Filter by search
  const filteredCohort = useMemo(() => {
    let list = [];
    switch (activeTab) {
      case 'needs-jan2': list = categorizedCohort.needsJan2; break;
      case 'needs-feb1': list = categorizedCohort.needsFeb1; break;
      case 'needs-mar1': list = categorizedCohort.needsMar1; break;
      case 'complete': list = categorizedCohort.allComplete; break;
      case 'all': list = cohort; break;
      default: list = cohort;
    }

    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(c =>
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.includes(term)
    );
  }, [cohort, categorizedCohort, activeTab, searchTerm]);

  // Send follow-up (default to SMS for OEP blasts)
  const handleSendFollowUp = async (member, type, channel = 'sms') => {
    try {
      setSendingTo(`${member.id}-${type}`);
      await api.sendFollowUp(member.id, {
        automationType: type,
        channel: channel
      });
      await loadData(); // Refresh
      const channelLabel = channel === 'sms' ? 'SMS' : 'Email';
      alert(`${type.toUpperCase()} ${channelLabel} sent to ${member.firstName}!`);
    } catch (err) {
      alert(`Failed to send: ${err.message}`);
    } finally {
      setSendingTo(null);
    }
  };

  // Auto-populate cohort
  const handleAutoPopulate = async () => {
    try {
      const result = await api.autoPopulateCohort(seasonYear);
      alert(result.message || `Added ${result.added} clients to cohort`);
      await loadData();
    } catch (err) {
      alert(`Failed to populate: ${err.message}`);
    }
  };

  // Update client status
  const handleUpdateStatus = async (member, newStatus) => {
    try {
      await api.updateCohortMember(member.id, { status: newStatus });
      await loadData();
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
  };

  // Update outreach plan (opt in/out of individual blasts)
  const handleUpdateOutreach = async (member, blastType, enabled) => {
    try {
      const outreachPlan = {
        jan2: member.outreachPlan?.jan2 ?? true,
        feb1: member.outreachPlan?.feb1 ?? true,
        mar1: member.outreachPlan?.mar1 ?? true,
        [blastType]: enabled
      };
      await api.updateCohortMember(member.id, { outreachPlan });
      // Update local state immediately for better UX
      setCohort(prev => prev.map(c =>
        c.id === member.id
          ? { ...c, outreachPlan }
          : c
      ));
    } catch (err) {
      alert(`Failed to update outreach settings: ${err.message}`);
    }
  };

  // Remove from cohort
  const handleRemoveFromCohort = async (member) => {
    if (!window.confirm(`Remove ${member.firstName} ${member.lastName} from OEP cohort?`)) return;
    try {
      await api.removeCohortMember(member.id);
      setCohort(prev => prev.filter(c => c.id !== member.id));
    } catch (err) {
      alert(`Failed to remove: ${err.message}`);
    }
  };

  // Remove duplicates
  const handleRemoveDuplicates = async () => {
    try {
      const result = await api.removeDuplicates(seasonYear);
      alert(result.message || `Removed ${result.removed} duplicate entries`);
      await loadData();
    } catch (err) {
      alert(`Failed to remove duplicates: ${err.message}`);
    }
  };

  // Clear and resync cohort (only Jan 1 effective dates)
  const handleClearAndResync = async () => {
    if (!window.confirm(`This will clear ALL clients from OEP ${seasonYear} cohort and re-add only clients with 1/1/${seasonYear} effective date. Continue?`)) return;
    try {
      const clearResult = await api.clearCohort(seasonYear);
      const syncResult = await api.autoPopulateCohort(seasonYear);
      alert(`Cleared ${clearResult.removed} entries. ${syncResult.message}`);
      await loadData();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
  };

  // Send referral blast
  const handleSendReferralBlast = async (channel) => {
    setSendingReferral(true);
    setReferralResult(null);

    try {
      const response = await fetch(`${BASE}/api/automations/referral-blast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ channel })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setReferralResult({ success: true, message: `Referral blast sent to ${data.count || 'all'} clients!` });
      } else {
        setReferralResult({ success: false, message: data.error || 'Failed to send referral blast' });
      }
    } catch (error) {
      console.error('[OEPHub] Referral blast error:', error);
      setReferralResult({ success: false, message: 'An error occurred. Please try again.' });
    } finally {
      setSendingReferral(false);
    }
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = cohort.length || jan1Clients.length;
    const active = categorizedCohort.active.length;
    const switched = categorizedCohort.switched.length;
    const lost = categorizedCohort.lost.length;
    const retained = active + switched; // Switched but stayed with agent
    const retentionRate = total > 0 ? Math.round((retained / total) * 100) : 0;

    return {
      total,
      active,
      switched,
      lost,
      retained,
      retentionRate,
      jan2Complete: cohort.filter(c => c.jan2Sent).length,
      feb1Complete: cohort.filter(c => c.feb1Sent).length,
      mar1Complete: cohort.filter(c => c.mar1Sent).length,
    };
  }, [cohort, jan1Clients, categorizedCohort]);

  // Outreach progress
  const outreachProgress = useMemo(() => {
    const total = cohort.length || 1;
    return {
      jan2: Math.round((metrics.jan2Complete / total) * 100),
      feb1: Math.round((metrics.feb1Complete / total) * 100),
      mar1: Math.round((metrics.mar1Complete / total) * 100),
    };
  }, [cohort.length, metrics]);

  const inSeason = now >= localSeason.start && now <= localSeason.end;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading OEP Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ========== HEADER ========== */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: brand.navy }}>
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-slate-900">OEP Hub</h1>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>Jan 1 – Mar 31, {seasonYear}</span>
                  {inSeason && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                      ACTIVE SEASON
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={seasonYear}
                onChange={(e) => setSeasonYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium"
              >
                <option value={2025}>2025 Season</option>
                <option value={2026}>2026 Season</option>
                <option value={2027}>2027 Season</option>
              </select>
              <button
                onClick={() => setShowReferralModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Referral Blast</span>
              </button>
              <button
                onClick={handleAutoPopulate}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Sync Clients</span>
              </button>
              <button
                onClick={handleRemoveDuplicates}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
                title="Remove duplicate entries from cohort"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Remove Duplicates</span>
              </button>
              <button
                onClick={handleClearAndResync}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition"
                title="Clear all and re-add only clients with 1/1 effective date"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Clear & Resync</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ========== HERO METRICS ========== */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="rounded-2xl p-6 md:p-8 shadow-xl text-white" style={{ background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.slate} 100%)` }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
            <div>
              <div className="text-sm text-white/70 uppercase tracking-wide font-semibold">OEP {seasonYear}</div>
              <h2 className="text-3xl md:text-4xl font-black">
                {metrics.total} New Clients
                <span className="text-lg font-normal text-white/70 ml-2">effective 1/1/{seasonYear}</span>
              </h2>
              <p className="text-white/80 mt-1">
                Keep every client you worked hard to enroll during AEP
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-4xl font-black" style={{ color: brand.gold }}>{metrics.retentionRate}%</div>
                <div className="text-sm text-white/70">Retention Rate</div>
              </div>
              <TrendingUp className="w-10 h-10 text-green-400" />
            </div>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Active & Retained"
              value={metrics.active}
              icon={<CheckCircle2 className="w-5 h-5" />}
              color="bg-green-500"
              sublabel="Still with you"
            />
            <MetricCard
              label="Switched Plans"
              value={metrics.switched}
              icon={<RefreshCw className="w-5 h-5" />}
              color="bg-blue-500"
              sublabel="Stayed with agent"
            />
            <MetricCard
              label="Lost Clients"
              value={metrics.lost}
              icon={<UserMinus className="w-5 h-5" />}
              color="bg-red-500"
              sublabel="Churned"
            />
            <MetricCard
              label="Total Enrolled"
              value={metrics.total}
              icon={<Users className="w-5 h-5" />}
              color="bg-indigo-500"
              sublabel={`From AEP ${seasonYear - 1}`}
            />
          </div>
        </div>
      </div>

      {/* ========== OUTREACH PROGRESS ========== */}
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Outreach Progress
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <OutreachCard
              title="Day 2 Welcome"
              date="January 2nd"
              description="Welcome to new plan, ID cards received, any help needed"
              completed={metrics.jan2Complete}
              total={metrics.total}
              percentage={outreachProgress.jan2}
              color="bg-green-500"
              pending={categorizedCohort.needsJan2.length}
              onBlast={() => setActiveTab('needs-jan2')}
            />
            <OutreachCard
              title="February Check-in"
              date="February 1st"
              description="First full month check-in, pharmacy issues, doctor visits"
              completed={metrics.feb1Complete}
              total={metrics.total}
              percentage={outreachProgress.feb1}
              color="bg-blue-500"
              pending={categorizedCohort.needsFeb1.length}
              onBlast={() => setActiveTab('needs-feb1')}
            />
            <OutreachCard
              title="March Follow-up"
              date="March 1st"
              description="Final check-in, satisfaction survey, referral ask"
              completed={metrics.mar1Complete}
              total={metrics.total}
              percentage={outreachProgress.mar1}
              color="bg-purple-500"
              pending={categorizedCohort.needsMar1.length}
              onBlast={() => setActiveTab('needs-mar1')}
            />
          </div>
        </div>
      </div>

      {/* ========== CLIENT PIPELINE ========== */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-slate-200 bg-slate-50">
            <div className="flex flex-wrap gap-1 p-2">
              <TabButton
                active={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
                count={cohort.length}
                label="All OEP Clients"
                color="bg-indigo-600"
              />
              <TabButton
                active={activeTab === 'needs-jan2'}
                onClick={() => setActiveTab('needs-jan2')}
                count={categorizedCohort.needsJan2.length}
                label="Needs Jan 2"
                color="bg-amber-500"
              />
              <TabButton
                active={activeTab === 'needs-feb1'}
                onClick={() => setActiveTab('needs-feb1')}
                count={categorizedCohort.needsFeb1.length}
                label="Needs Feb 1"
                color="bg-blue-500"
              />
              <TabButton
                active={activeTab === 'needs-mar1'}
                onClick={() => setActiveTab('needs-mar1')}
                count={categorizedCohort.needsMar1.length}
                label="Needs Mar 1"
                color="bg-purple-500"
              />
              <TabButton
                active={activeTab === 'complete'}
                onClick={() => setActiveTab('complete')}
                count={categorizedCohort.allComplete.length}
                label="Complete"
                color="bg-green-500"
              />
            </div>
          </div>

          {/* Search & Actions */}
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div className="text-sm text-slate-600">
              Showing <span className="font-semibold">{filteredCohort.length}</span> clients
            </div>
          </div>

          {/* Client List */}
          <div className="divide-y divide-slate-100">
            {filteredCohort.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-slate-700">No clients in this category</h4>
                <p className="text-slate-500 text-sm mt-1">
                  {activeTab === 'needs-jan2' && "All clients have received their Day 2 welcome!"}
                  {activeTab === 'needs-feb1' && "Complete Jan 2 outreach first, then clients will appear here."}
                  {activeTab === 'needs-mar1' && "Complete Feb 1 outreach first, then clients will appear here."}
                  {activeTab === 'complete' && "No clients have completed all outreach yet."}
                  {activeTab === 'all' && "Click 'Sync Clients' to populate your OEP cohort."}
                </p>
                {activeTab === 'all' && cohort.length === 0 && (
                  <button
                    onClick={handleAutoPopulate}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
                  >
                    Sync Clients Now
                  </button>
                )}
              </div>
            ) : (
              filteredCohort.map((member) => (
                <ClientRow
                  key={member.id}
                  member={member}
                  onSend={handleSendFollowUp}
                  onUpdateStatus={handleUpdateStatus}
                  onUpdateOutreach={handleUpdateOutreach}
                  onDelete={handleRemoveFromCohort}
                  sendingTo={sendingTo}
                  onViewProfile={() => navigate(`/clients/${member.clientId}`)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ========== RECENT ACTIVITY ========== */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            Recent Activity
          </h3>
          {activity.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No activity yet. Start sending outreach!</p>
          ) : (
            <div className="space-y-3">
              {activity.slice(0, 10).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      item.status === 'sent' ? 'bg-green-100 text-green-600' :
                      item.status === 'failed' ? 'bg-red-100 text-red-600' :
                      'bg-slate-200 text-slate-600'
                    }`}>
                      {item.type === 'SMS' ? <MessageCircle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800 text-sm">{item.automation || 'Manual Send'}</div>
                      <div className="text-xs text-slate-500">{item.to} • {formatDate(item.time)}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    item.status === 'sent' ? 'bg-green-100 text-green-700' :
                    item.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-slate-200 text-slate-700'
                  }`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========== REFERRAL BLAST MODAL ========== */}
      {showReferralModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowReferralModal(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Send Referral Blast</h2>
                <p className="text-sm text-slate-500 mt-1">Ask your clients for referrals</p>
              </div>
              <button
                onClick={() => setShowReferralModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {referralResult && (
                <div className={`p-3 rounded-xl text-sm ${
                  referralResult.success
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {referralResult.message}
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600 font-medium mb-2">SMS Preview:</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {`Hi {ClientFirstName}, it's {AgentName}. Just wanted to reach out and ask if you know anyone who has Medicare or may be interested in speaking/meeting with me to discuss their plan. My business is sustained by referral business and anyone would be greatly appreciated! Either way, how is everything going with your plan? Any concerns?`}
                </p>
              </div>

              <p className="text-sm text-slate-500">
                This will send a referral request to all your active clients. Messages will be personalized with each client's name and your contact info.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
              <button
                onClick={() => setShowReferralModal(false)}
                className="px-5 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSendReferralBlast('sms')}
                disabled={sendingReferral}
                className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
                {sendingReferral ? 'Sending...' : 'Send SMS Blast'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function MetricCard({ label, value, icon, color, sublabel }) {
  return (
    <div className="bg-white/10 rounded-xl p-4 border border-white/20">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/70 text-sm">{label}</span>
        <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center text-white`}>
          {icon}
        </div>
      </div>
      <div className="text-3xl font-black text-white">{value}</div>
      {sublabel && <div className="text-xs text-white/60 mt-1">{sublabel}</div>}
    </div>
  );
}

function OutreachCard({ title, date, description, completed, total, percentage, color, pending, onBlast }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-bold text-slate-900">{title}</h4>
          <p className="text-xs text-slate-500">{date}</p>
        </div>
        <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center text-white font-bold`}>
          {percentage}%
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-3">{description}</p>
      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{completed} / {total} sent</span>
          <span>{pending} pending</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${percentage}%` }} />
        </div>
      </div>
      {pending > 0 && (
        <button
          onClick={onBlast}
          className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold text-slate-700 transition flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          View {pending} Pending
        </button>
      )}
    </div>
  );
}

function TabButton({ active, onClick, count, label, color }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${
        active
          ? 'bg-white shadow text-slate-900'
          : 'text-slate-600 hover:bg-white/50'
      }`}
    >
      {label}
      <span className={`px-2 py-0.5 rounded-full text-xs text-white ${color}`}>
        {count}
      </span>
    </button>
  );
}

function ClientRow({ member, onSend, onUpdateStatus, onUpdateOutreach, onDelete, sendingTo, onViewProfile }) {
  const [expanded, setExpanded] = useState(false);

  // Check if outreach is enabled for each type (default to true if not set)
  const outreachEnabled = {
    jan2: member.outreachPlan?.jan2 !== false,
    feb1: member.outreachPlan?.feb1 !== false,
    mar1: member.outreachPlan?.mar1 !== false
  };

  return (
    <div className="hover:bg-slate-50 transition">
      {/* Main Row - Mobile friendly */}
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition flex-shrink-0"
          >
            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Avatar */}
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-white text-xs sm:text-sm flex-shrink-0"
            style={{ background: brand.navy }}>
            {member.firstName?.[0]}{member.lastName?.[0]}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-sm sm:text-base truncate">{member.firstName} {member.lastName}</span>
              <StatusBadge status={member.status} />
            </div>
            <div className="text-xs sm:text-sm text-slate-500 truncate">
              {member.email || member.phone || 'No contact info'}
            </div>
          </div>

          {/* Desktop: View & Delete buttons */}
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={onViewProfile}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
              title="View Profile"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(member)}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
              title="Remove from cohort"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile: Outreach buttons below name */}
        <div className="mt-2 flex items-center gap-2 flex-wrap ml-9 sm:ml-14">
          {/* Outreach Status Indicators - hidden on mobile, shown on desktop */}
          <div className="hidden md:flex items-center gap-2 mr-2">
            <OutreachIndicator label="Jan 2" completed={member.jan2Sent} />
            <OutreachIndicator label="Feb 1" completed={member.feb1Sent} />
            <OutreachIndicator label="Mar 1" completed={member.mar1Sent} />
          </div>

          {/* Quick Actions */}
          {!member.jan2Sent && outreachEnabled.jan2 && (
            <button
              onClick={() => onSend(member, 'jan2')}
              disabled={sendingTo === `${member.id}-jan2`}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50 flex items-center gap-1"
            >
              {sendingTo === `${member.id}-jan2` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              <span className="hidden xs:inline">Jan 2</span>
              <span className="xs:hidden">J2</span>
            </button>
          )}
          {!member.jan2Sent && !outreachEnabled.jan2 && (
            <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-100 text-slate-400 text-xs font-semibold rounded-lg flex items-center gap-1" title="Jan 2 blast disabled">
              <BellOff className="w-3 h-3" />
              <span className="hidden sm:inline">Jan 2 Off</span>
            </span>
          )}
          {member.jan2Sent && !member.feb1Sent && outreachEnabled.feb1 && (
            <button
              onClick={() => onSend(member, 'feb1')}
              disabled={sendingTo === `${member.id}-feb1`}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50 flex items-center gap-1"
            >
              {sendingTo === `${member.id}-feb1` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              <span className="hidden xs:inline">Feb 1</span>
              <span className="xs:hidden">F1</span>
            </button>
          )}
          {member.jan2Sent && !member.feb1Sent && !outreachEnabled.feb1 && (
            <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-100 text-slate-400 text-xs font-semibold rounded-lg flex items-center gap-1" title="Feb 1 blast disabled">
              <BellOff className="w-3 h-3" />
              <span className="hidden sm:inline">Feb 1 Off</span>
            </span>
          )}
          {member.jan2Sent && member.feb1Sent && !member.mar1Sent && outreachEnabled.mar1 && (
            <button
              onClick={() => onSend(member, 'mar1')}
              disabled={sendingTo === `${member.id}-mar1`}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50 flex items-center gap-1"
            >
              {sendingTo === `${member.id}-mar1` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              <span className="hidden xs:inline">Mar 1</span>
              <span className="xs:hidden">M1</span>
            </button>
          )}
          {member.jan2Sent && member.feb1Sent && !member.mar1Sent && !outreachEnabled.mar1 && (
            <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-100 text-slate-400 text-xs font-semibold rounded-lg flex items-center gap-1" title="Mar 1 blast disabled">
              <BellOff className="w-3 h-3" />
              <span className="hidden sm:inline">Mar 1 Off</span>
            </span>
          )}
          {member.jan2Sent && member.feb1Sent && member.mar1Sent && (
            <span className="px-2 sm:px-3 py-1 sm:py-1.5 bg-green-100 text-green-700 text-xs font-semibold rounded-lg flex items-center gap-1">
              <CheckCheck className="w-3 h-3" />
              <span className="hidden sm:inline">Complete</span>
              <span className="sm:hidden">Done</span>
            </span>
          )}

          {/* Mobile: View & Delete buttons */}
          <div className="sm:hidden flex items-center gap-1 ml-auto">
            <button
              onClick={onViewProfile}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
              title="View Profile"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(member)}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
              title="Remove from cohort"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 ml-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500 mb-1">Contact</div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-slate-400" />
              {formatPhone(member.phone)}
            </div>
            <div className="flex items-center gap-2 text-sm mt-1">
              <Mail className="w-4 h-4 text-slate-400" />
              {member.email || '—'}
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500 mb-1">Plan Details</div>
            <div className="text-sm font-medium">{member.carrier || 'Unknown Carrier'}</div>
            <div className="text-sm text-slate-600">{member.plan || 'Plan not specified'}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500 mb-1">Status</div>
            <div className="flex items-center gap-2">
              <select
                value={member.status}
                onChange={(e) => onUpdateStatus(member, e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              >
                <option value="Active">Active</option>
                <option value="Switched">Switched Plans</option>
                <option value="Cancelled">Lost/Cancelled</option>
              </select>
            </div>
          </div>

          {/* Outreach History */}
          <div className="md:col-span-3 bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500 mb-2">Outreach History</div>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                {member.jan2Sent ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className="text-sm">
                  Jan 2: {member.jan2Sent ? formatDate(member.jan2SentAt) : 'Pending'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {member.feb1Sent ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className="text-sm">
                  Feb 1: {member.feb1Sent ? formatDate(member.feb1SentAt) : 'Pending'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {member.mar1Sent ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <Circle className="w-4 h-4 text-slate-300" />
                )}
                <span className="text-sm">
                  Mar 1: {member.mar1Sent ? formatDate(member.mar1SentAt) : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Outreach Settings - Toggle individual blasts on/off */}
          <div className="md:col-span-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-3">
              <BellOff className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-semibold text-amber-800">Outreach Settings</span>
              <span className="text-xs text-amber-600">— Turn off individual blasts for this client</span>
            </div>
            <div className="flex flex-wrap gap-4">
              {/* Jan 2 Toggle */}
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                member.jan2Sent
                  ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                  : outreachEnabled.jan2
                    ? 'bg-amber-100 border-amber-300 hover:bg-amber-200'
                    : 'bg-slate-100 border-slate-300 hover:bg-slate-200'
              }`}>
                <input
                  type="checkbox"
                  checked={outreachEnabled.jan2}
                  onChange={(e) => onUpdateOutreach(member, 'jan2', e.target.checked)}
                  disabled={member.jan2Sent}
                  className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${member.jan2Sent ? 'text-slate-400' : 'text-slate-700'}`}>
                  Jan 2 Welcome
                </span>
                {member.jan2Sent && <span className="text-xs text-slate-400">(sent)</span>}
              </label>

              {/* Feb 1 Toggle */}
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                member.feb1Sent
                  ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                  : outreachEnabled.feb1
                    ? 'bg-blue-100 border-blue-300 hover:bg-blue-200'
                    : 'bg-slate-100 border-slate-300 hover:bg-slate-200'
              }`}>
                <input
                  type="checkbox"
                  checked={outreachEnabled.feb1}
                  onChange={(e) => onUpdateOutreach(member, 'feb1', e.target.checked)}
                  disabled={member.feb1Sent}
                  className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${member.feb1Sent ? 'text-slate-400' : 'text-slate-700'}`}>
                  Feb 1 Check-in
                </span>
                {member.feb1Sent && <span className="text-xs text-slate-400">(sent)</span>}
              </label>

              {/* Mar 1 Toggle */}
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                member.mar1Sent
                  ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                  : outreachEnabled.mar1
                    ? 'bg-purple-100 border-purple-300 hover:bg-purple-200'
                    : 'bg-slate-100 border-slate-300 hover:bg-slate-200'
              }`}>
                <input
                  type="checkbox"
                  checked={outreachEnabled.mar1}
                  onChange={(e) => onUpdateOutreach(member, 'mar1', e.target.checked)}
                  disabled={member.mar1Sent}
                  className="w-4 h-4 text-purple-600 rounded border-slate-300 focus:ring-purple-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${member.mar1Sent ? 'text-slate-400' : 'text-slate-700'}`}>
                  Mar 1 OEP Reminder
                </span>
                {member.mar1Sent && <span className="text-xs text-slate-400">(sent)</span>}
              </label>
            </div>
            <p className="text-xs text-amber-700 mt-2">
              Uncheck to skip automated blasts for this client. Useful when client has already been contacted or is switching carriers.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    Active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
    Switched: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Switched' },
    Cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Lost' },
    Lost: { bg: 'bg-red-100', text: 'text-red-700', label: 'Lost' },
  };
  const c = config[status] || config.Active;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function OutreachIndicator({ label, completed }) {
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
      completed ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
    }`}>
      {completed ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {label}
    </div>
  );
}
